// Fetch wrapper for the JSON API. Errors carry the server's { code, message }
// so the UI can show the message and branch on the code.

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message)
    this.status = status
    this.code = code
    this.details = details
  }
}

const unauthorizedListeners = new Set()

/** Called when any request returns 401 (session expired). */
export function onUnauthorized(listener) {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

/** Tell the app the session ended (requests made outside api()). */
export function reportUnauthorized(error) {
  unauthorizedListeners.forEach((fn) => fn(error))
}

export async function api(path, { method = 'GET', body, signal, headers } = {}) {
  let response
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      signal,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause
    throw new ApiError(0, 'network', 'You appear to be offline. Check your connection and try again.')
  }
  if (response.status === 204) return null
  const data = await response.json().catch(() => null)
  if (!response.ok) {
    const error = new ApiError(
      response.status,
      data?.error?.code ?? 'http_error',
      data?.error?.message ?? `Request failed (${response.status}).`,
      data?.error?.details,
    )
    if (response.status === 401) reportUnauthorized(error)
    throw error
  }
  return data
}

/** Message for any error thrown by api(), a stream, or code. */
export function errorMessage(error, fallback = 'Something went wrong.') {
  if (!error) return fallback
  if (error instanceof ApiError) return error.message
  const text = String(error.message ?? error)
  // Stream errors may carry the server's JSON envelope as text.
  const match = /"message"\s*:\s*"([^"]+)"/.exec(text)
  return match ? match[1] : text || fallback
}

export const isNotFound = (error) => error instanceof ApiError && (error.status === 404 || error.status === 403)
